import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { STATUS_LABEL, type TicketStatus } from '@veta-desk/domain';
import { Shell } from '../components/Shell';
import { api, type ContractDetail as Detail } from '../lib/api';
import { LEVEL, MODEL_LABEL, Meter, h, monthLabel } from '../lib/contracts';
import { StatementsSection } from '../components/StatementsSection';
import { ContractDocuments } from '../components/ContractDocuments';

// Etapa 2 · detalle de un contrato: estado de cuenta mensual (baseline) o consumo de la bolsa,
// y los requerimientos cotizados que descuentan lo cotizado.

const th = 'border-b border-line px-3 py-2.5 text-right font-semibold';
const td = 'px-3 py-2 text-right font-mono tabular-nums';

export default function ContractDetail() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['contract', id], queryFn: () => api<Detail>(`/contracts/${id}`) });

  if (q.isLoading) return <Shell><p role="status" className="py-10 text-center text-sm text-muted">Cargando…</p></Shell>;
  if (q.isError || !q.data) return <Shell><p role="alert" className="rounded-lg border border-line bg-white p-8 text-center text-sm">{(q.error as Error)?.message ?? 'No encontramos ese contrato.'}</p></Shell>;
  const d = q.data;
  const b = d.baseline;

  return (
    <Shell>
      <div className="flex flex-col gap-5">
        <Link to="/contratos" className="text-sm text-accent hover:underline">← Contratos</Link>
        <header>
          <p className="font-mono text-sm text-muted">{d.project.code} · {MODEL_LABEL[d.model]}</p>
          <h1 className="mt-1 text-2xl font-semibold">{d.project.clientName ?? d.project.name}</h1>
          <p className="mt-1 text-sm text-muted">
            {d.project.name}
            {b?.terms ? ` · ${h(b.terms.monthlyHours)} por mes, ${b.terms.months} meses desde ${monthLabel(b.terms.startMonth)} · rollover ${b.terms.rolloverPct} % · banda ${b.terms.flexBandPct} % ${b.terms.bandSource === 'pool' ? '(bolsa segmentada)' : '(mensual estricto)'}` : ''}
            {d.project.startDate && d.model === 'bag' ? ` · vigencia ${d.project.startDate} a ${d.project.endDate ?? 'abierta'}` : ''}
          </p>
        </header>

        {/* Resumen */}
        <section className="grid gap-3 sm:grid-cols-3">
          {d.bag && <>
            <Stat label="Consumido" value={h(d.bag.used)} sub={`de ${h(d.bag.maxHours)} · ${Math.round(d.bag.pct * 100)} %`} />
            <Stat label="Disponible" value={h(d.bag.remaining)} tone={LEVEL[d.bag.level].text} sub={LEVEL[d.bag.level].label} />
            <Stat label={`Este mes (${monthLabel(d.month)})`} value={h(d.thisMonth.charged)} sub={`${h(d.thisMonth.actual)} capturadas`} />
          </>}
          {b?.current && <>
            <Stat label={`Disponible en ${monthLabel(b.current.month)}`} value={h(b.current.available)} sub={`base ${h(b.current.base)}${b.current.carryIn ? ` + rollover ${h(b.current.carryIn)}` : ''}${b.current.advanceIn ? ` − anticipo ${h(b.current.advanceIn)}` : ''}`} />
            <Stat label="Usado este mes" value={h(b.current.used)} tone={LEVEL[b.level].text} sub={b.current.outOfBand ? `${h(b.current.outOfBand)} fuera de banda (tarifa de excedente)` : b.current.inBand ? `${h(b.current.inBand)} en banda` : `saldo ${h(b.current.balance)}`} />
            <Stat label={b.terms?.bandSource === 'pool' ? 'Quedan en el contrato' : 'Fin del contrato'}
              value={b.terms?.bandSource === 'pool' ? h(b.poolRemaining ?? 0) : monthLabel(b.endMonth)}
              tone={b.projectedExhaustMonth ? 'text-[#8F1D1D]' : undefined}
              sub={b.projectedExhaustMonth ? `a este ritmo se agota en ${monthLabel(b.projectedExhaustMonth)}` : `termina en ${monthLabel(b.endMonth)}`} />
          </>}
          {b && !b.current && <p className="rounded-lg border border-line bg-white p-4 text-sm text-muted sm:col-span-3">{LEVEL[b.level].label}.</p>}
          {d.model === 'on_demand' && <Stat label={`Este mes (${monthLabel(d.month)})`} value={h(d.thisMonth.charged)} sub="On Demand: se factura lo consumido" />}
        </section>
        {d.bag && <Meter pct={d.bag.pct} level={d.bag.level} label="Consumo de la bolsa" />}

        {/* Estado de cuenta mensual del baseline */}
        {b && b.rows.length > 0 && (
          <section className="rounded-lg border border-line bg-white">
            <h2 className="px-4 pt-4 text-base font-semibold">Estado de cuenta mensual</h2>
            <div className="overflow-x-auto p-2">
              <table className="w-full min-w-[720px] border-collapse text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-muted">
                    <th scope="col" className="border-b border-line px-3 py-2.5 text-left font-semibold">Mes</th>
                    <th scope="col" className={th}>Base</th><th scope="col" className={th}>Rollover</th>
                    {b.terms?.bandSource === 'next_month' && <th scope="col" className={th}>Anticipo</th>}
                    <th scope="col" className={th}>Disponible</th><th scope="col" className={th}>Usado</th><th scope="col" className={th}>Saldo</th>
                    <th scope="col" className={th}>En banda</th><th scope="col" className={th}>Fuera de banda</th><th scope="col" className={th}>Rollover sig.</th>
                  </tr>
                </thead>
                <tbody>
                  {[...b.rows].reverse().map(r => (
                    <tr key={r.month} className={`border-b border-[#EFEDE9] last:border-0 ${r.state === 'current' ? 'bg-accent-soft/40' : ''}`}>
                      <td className="px-3 py-2 text-left">{monthLabel(r.month)}{r.state === 'current' && <span className="ml-2 text-xs text-accent-dark">en curso</span>}</td>
                      <td className={td}>{r.base}</td><td className={td}>{r.carryIn || '—'}</td>
                      {b.terms?.bandSource === 'next_month' && <td className={td}>{r.advanceIn ? `−${r.advanceIn}` : '—'}</td>}
                      <td className={td}>{r.available}</td><td className={`${td} font-semibold`}>{r.used}</td>
                      <td className={`${td} ${r.balance < 0 ? 'text-[#8F1D1D]' : ''}`}>{r.balance}</td>
                      <td className={`${td} ${r.inBand ? 'text-[#6A3A00]' : ''}`}>{r.inBand || '—'}</td>
                      <td className={`${td} ${r.outOfBand ? 'font-semibold text-[#8F1D1D]' : ''}`}>{r.outOfBand || '—'}</td>
                      <td className={td}>{r.carryOut || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="px-4 pb-4 text-xs text-muted">
              El rollover del mes en curso es provisional hasta que cierre. Al terminar el contrato, el saldo no usado se pierde.
              {b.lastMonthAdvance > 0 ? ` La banda del último mes (${h(b.lastMonthAdvance)}) no tiene mes siguiente: se resuelve en la renovación o se factura.` : ''}
            </p>
          </section>
        )}

        <StatementsSection projectId={d.project.id} />
        <ContractDocuments projectId={d.project.id} />

        <div className={`grid gap-5 ${d.model !== 'baseline' ? 'lg:grid-cols-2' : ''}`}>
          {/* Por mes (bolsa y On Demand) */}
          {d.model !== 'baseline' && (
            <section className="rounded-lg border border-line bg-white p-4">
              <h2 className="text-base font-semibold">Por mes</h2>
              {d.months.length === 0 ? <p className="mt-2 text-sm text-muted">Sin horas capturadas.</p> : (
                <table className="mt-2 w-full border-collapse text-sm">
                  <thead><tr className="text-xs uppercase tracking-wide text-muted"><th scope="col" className="border-b border-line py-2 text-left font-semibold">Mes</th><th scope="col" className={th}>Capturadas</th><th scope="col" className={th}>Cuentan</th></tr></thead>
                  <tbody>
                    {d.months.map(m => (
                      <tr key={m.month} className="border-b border-[#EFEDE9] last:border-0">
                        <td className="py-2">{monthLabel(m.month)}</td><td className={td}>{m.actual}</td><td className={`${td} font-semibold`}>{m.charged}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          )}

          {/* Requerimientos cotizados que consumen contrato */}
          <section className="rounded-lg border border-line bg-white p-4">
            <h2 className="text-base font-semibold">Requerimientos cotizados</h2>
            <p className="mt-0.5 text-xs text-muted">Descuentan lo cotizado: lo que pase lo absorbe Ascendix; al cerrar se completa lo que falte.</p>
            {d.reqs.length === 0 ? <p className="mt-2 text-sm text-muted">Ninguno.</p> : (
              <ul className="mt-2 divide-y divide-[#EFEDE9] text-sm">
                {d.reqs.map(r => {
                  const over = r.actualHours > r.quotedHours * 1.1;
                  return (
                    <li key={r.number} className="flex items-start justify-between gap-3 py-2">
                      <span className="min-w-0">
                        <Link to={`/tickets/${r.number}`} className="font-mono text-xs text-accent hover:underline">{r.number}</Link>
                        <span className="block truncate">{r.subject}</span>
                        <span className="text-xs text-muted">{STATUS_LABEL[r.status as TicketStatus] ?? r.status}{r.closedMonth ? ` · cerrado ${monthLabel(r.closedMonth)}` : ''}</span>
                      </span>
                      <span className="shrink-0 text-right text-xs">
                        <span className="block font-semibold">{h(r.quotedHours)} cotizadas</span>
                        <span className={over ? 'font-semibold text-[#8F1D1D]' : 'text-muted'}>{h(r.actualHours)} reales</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </Shell>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="rounded-lg border border-line bg-white p-4">
      <p className="text-xs text-muted">{label}</p>
      <p className={`mt-1 font-mono text-2xl font-semibold tabular-nums ${tone ?? 'text-ink'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted">{sub}</p>}
    </div>
  );
}
